import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BrandLogo from '@/components/base/BrandLogo';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useLiveStreamSettings } from '@/hooks/useLiveStreamSettings';
import { fetchPublishedPages } from '@/lib/api';

interface NavChild {
  label: string;
  path: string;
}

interface NavItem {
  label: string;
  path: string;
  children?: NavChild[];
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Services', path: '/services' },
  { label: 'Sermons', path: '/sermons' },
  { label: 'Books', path: '/books' },
  {
    label: 'Events',
    path: '/events',
    children: [
      { label: 'Upcoming Events', path: '/events' },
      { label: 'Gallery', path: '/highlights' },
    ],
  },
  {
    label: 'Give',
    path: '/give',
    children: [
      { label: 'Give Tithes & Offerings', path: '/give' },
      { label: 'Sponsor a Project', path: '/events' },
    ],
  },
  { label: 'Contact', path: '/contact' },
];

// Primary navigation shown on desktop (with dropdowns for Events + Give).
const PRIMARY_NAV = NAV_ITEMS.filter((link) =>
  ['Home', 'About', 'Services', 'Sermons', 'Books', 'Events', 'Give', 'Contact'].includes(link.label),
);

// Flat list shown in the mobile slide-in menu.
const MOBILE_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Services', path: '/services' },
  { label: 'Sermons', path: '/sermons' },
  { label: 'Events', path: '/events' },
  { label: 'Gallery', path: '/highlights' },
  { label: 'Give', path: '/give' },
  { label: 'Books', path: '/books' },
  { label: 'Testimonials', path: '/testimonials' },
  { label: 'News', path: '/news' },
  { label: 'Contact', path: '/contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [customPages, setCustomPages] = useState<NavItem[]>([]);
  const location = useLocation();
  const { data: settings } = useSiteSettings();
  const { data: live } = useLiveStreamSettings();
  const isLive = live?.is_live ?? false;

  useEffect(() => {
    fetchPublishedPages()
      .then((pages) =>
        setCustomPages(
          pages
            .filter((p) => p.show_in_nav)
            .map((p) => ({ label: p.title, path: `/page/${p.slug}` })),
        ),
      )
      .catch(() => setCustomPages([]));
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const itemIsActive = (item: NavItem) => {
    if (isActive(item.path)) return true;
    return item.children?.some((c) => isActive(c.path)) ?? false;
  };

  const announcementEnabled = settings?.announcement_enabled ?? true;
  const announcementText = settings?.announcement_text || 'Join Us Every Sunday';
  const announcementDetails = settings?.announcement_details || 'Worship Service at 10:00 AM';
  const phones = settings?.phones ?? [];
  const emails = settings?.emails ?? [];

  const hiddenPaths = settings?.hidden_nav_pages ?? [];
  const visiblePrimary = PRIMARY_NAV.filter((item) => !hiddenPaths.includes(item.path));
  const primaryNav = [...visiblePrimary, ...customPages];
  const visibleMobile = MOBILE_LINKS.filter((link) => !hiddenPaths.includes(link.path));
  const mobileNav = [...visibleMobile, ...customPages.map((p) => ({ label: p.label, path: p.path }))];

  return (
    <>
      {/* Announcement Bar */}
      {announcementEnabled && (
        <div className="bg-primary-800 text-white text-center py-2 px-4 text-xs font-label tracking-widest uppercase border-b-2 border-accent-500/40">
          <span className="text-accent-300 font-semibold">{announcementText}</span>
          {announcementDetails && (
            <>
              <span className="mx-2 text-accent-400">|</span>
              <span className="text-white/80">{announcementDetails}</span>
            </>
          )}
        </div>
      )}

      {/* Main Header */}
      <header
        className={`fixed ${announcementEnabled ? 'top-8' : 'top-0'} left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/97 backdrop-blur-md shadow-sm top-0'
            : `bg-transparent ${announcementEnabled ? 'top-8' : 'top-0'}`
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0">
              <BrandLogo
                url={settings?.logo_url}
                name={settings?.church_name || 'TGMCI'}
                settings={settings?.logo_settings}
              />
              <div className="flex flex-col">
                <span
                  className={`font-heading font-bold text-lg leading-tight transition-colors ${
                    scrolled ? 'text-primary-800' : 'text-white'
                  }`}
                >
                  {settings?.church_name || 'TGMCI'}
                </span>
                <span
                  className={`text-xs font-label leading-tight transition-colors hidden md:block ${
                    scrolled ? 'text-foreground-500' : 'text-white/80'
                  }`}
                >
                  {settings?.full_name || 'The Great Multitude Church'}
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" aria-label="Primary Navigation">
              {primaryNav.map((link) => {
                const active = itemIsActive(link);
                const base = `px-3 py-2 text-sm font-label font-medium tracking-wide transition-colors whitespace-nowrap flex items-center gap-1 ${
                  active
                    ? scrolled ? 'text-primary-600' : 'text-accent-400'
                    : scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-white/90 hover:text-white'
                }`;

                if (!link.children || link.children.length === 0) {
                  return (
                    <Link key={link.path} to={link.path} className={`${base} relative group`}>
                      {link.label}
                      <span
                        className={`absolute bottom-0 left-3 right-3 h-0.5 transition-transform origin-left ${
                          active ? 'scale-x-100 bg-accent-500' : 'scale-x-0 bg-accent-500 group-hover:scale-x-100'
                        }`}
                      />
                    </Link>
                  );
                }

                return (
                  <div key={link.path} className="relative group">
                    <Link to={link.path} className={base}>
                      {link.label}
                      <i className="ri-arrow-down-s-line text-xs" aria-hidden="true" />
                      <span
                        className={`absolute bottom-0 left-3 right-3 h-0.5 transition-transform origin-left ${
                          active ? 'scale-x-100 bg-accent-500' : 'scale-x-0 bg-accent-500 group-hover:scale-x-100'
                        }`}
                      />
                    </Link>
                    <div className="absolute top-full left-0 pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50">
                      <div className="bg-white rounded-lg border border-background-200 shadow-lg py-2 w-60 overflow-hidden">
                        {link.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-label whitespace-nowrap transition-colors ${
                              isActive(child.path)
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-foreground-700 hover:bg-background-100 hover:text-primary-600'
                            }`}
                          >
                            <i className="ri-arrow-right-s-line text-primary-400" aria-hidden="true" />
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </nav>

            {/* CTA Buttons + Hamburger */}
            <div className="flex items-center gap-3 shrink-0">
              {isLive && (
                <Link
                  to="/live"
                  className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-xs px-4 py-2 rounded-md font-label font-semibold whitespace-nowrap"
                >
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" aria-hidden="true" />
                  Watch Live
                </Link>
              )}
              <div className="hidden md:flex">
                <Link
                  to="/give"
                  className="flex items-center btn-accent text-xs px-4 py-2 whitespace-nowrap"
                >
                  <i className="ri-heart-line mr-1.5" aria-hidden="true" />
                  Support TGMCI
                </Link>
              </div>
              <div className="hidden lg:flex">
                <Link
                  to="/contact"
                  className="flex items-center btn-primary text-xs px-4 py-2"
                >
                  Get Involved
                </Link>
              </div>

              {/* Hamburger */}
              <button
                type="button"
                className={`lg:hidden flex flex-col gap-1.5 p-2 shrink-0 cursor-pointer rounded-lg transition-colors ${
                  scrolled ? 'hover:bg-background-100' : 'hover:bg-white/10'
                }`}
                aria-label="Toggle mobile menu"
                aria-expanded={mobileOpen}
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                <span
                  className={`block h-0.5 w-6 transition-all duration-300 ${
                    mobileOpen ? 'rotate-45 translate-y-2' : ''
                  } ${scrolled ? 'bg-foreground-800' : 'bg-white'}`}
                />
                <span
                  className={`block h-0.5 w-6 transition-all duration-300 ${
                    mobileOpen ? 'opacity-0' : ''
                  } ${scrolled ? 'bg-foreground-800' : 'bg-white'}`}
                />
                <span
                  className={`block h-0.5 w-6 transition-all duration-300 ${
                    mobileOpen ? '-rotate-45 -translate-y-2' : ''
                  } ${scrolled ? 'bg-foreground-800' : 'bg-white'}`}
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />

        {/* Slide-in Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-80 bg-white shadow-2xl flex flex-col transition-transform duration-300 ${
            mobileOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between p-6 border-b border-background-200">
            <div className="flex items-center gap-3">
              <BrandLogo
                url={settings?.logo_url}
                name={settings?.church_name || 'TGMCI'}
                settings={settings?.logo_settings}
                className="!w-8 !h-8"
              />
              <span className="font-heading font-bold text-primary-800">{settings?.church_name || 'TGMCI'}</span>
            </div>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="p-2 rounded-lg hover:bg-background-100 cursor-pointer text-foreground-600"
              aria-label="Close menu"
            >
              <i className="ri-close-line text-xl" aria-hidden="true" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto p-6" aria-label="Mobile Navigation">
            <ul className="space-y-1">
              {mobileNav.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`flex items-center px-4 py-3 rounded-lg text-sm font-label font-medium transition-colors ${
                      isActive(link.path)
                        ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-500'
                        : 'text-foreground-700 hover:bg-background-100 hover:text-primary-600'
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-6 border-t border-background-200 space-y-3">
            <Link to="/give" className="btn-accent w-full justify-center">
              <i className="ri-heart-line" aria-hidden="true" />
              Support TGMCI
            </Link>
            <Link to="/contact" className="btn-outline w-full justify-center">
              Get Involved
            </Link>
            <div className="text-center text-xs text-foreground-500 pt-2">
              {phones[0] && <p>{phones[0].number}</p>}
              {emails[0] && <p>{emails[0]}</p>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}